import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  cartIdSelector,
  resolveCatchMessage,
  setBillingReady,
  TCatchMessage,
  useLoadingContext,
  useToastContext,
} from '@segway/core'
import useCountryTransform from '@segway/core/src/businessHooks/useCountryTransform'
import {
  BillingAddressInput,
  CartAddressInput,
  SetBillingAddressOnCartMutation,
} from '@segway/core/src/graphql/generated/graphql'
import { useSetBillingAddressesOnCartMutation } from '@segway/core/src/services/client/checkoutApi'
import { useAppDispatch, useAppSelector } from '@segway/core/src/store/hooks'

/**
 * 结算页Billing地址 Hooks
 */
const useCheckoutBilling = () => {
  const dispatch = useAppDispatch()
  const loading = useLoadingContext()
  const toast = useToastContext()
  const cartId = useAppSelector(cartIdSelector)
  const { getCountryNameById } = useCountryTransform()

  const [isSameAsShipping, setIsSameAsShipping] = useState<boolean>(true)
  const [curBillingAddress, setCurBillingAddress] =
    useState<
      NonNullable<
        SetBillingAddressOnCartMutation['setBillingAddressOnCart']
      >['cart']['billing_address']
    >(null)

  const [setBillingAddressesOnCartMutation] = useSetBillingAddressesOnCartMutation()

  const addressTitle = useMemo(() => {
    if (curBillingAddress) {
      const { firstname, lastname, telephone } = curBillingAddress
      return `${firstname} ${lastname} ${telephone}`
    }
    return ''
  }, [curBillingAddress])

  const addressDescription = useMemo(() => {
    if (curBillingAddress) {
      const { street, city, country, postcode, company, region } = curBillingAddress
      return `${company ? `${company}, ` : ''}${street[0]} ${street?.[1] || ''} ${street?.[2] || ''}, ${region?.label ?? ''}, ${city}, ${getCountryNameById(country?.code ?? '')}, ${postcode}`
    }
    return ''
  }, [curBillingAddress, getCountryNameById])

  const differentBillingInfoVisible = useMemo(() => {
    return !isSameAsShipping && curBillingAddress
  }, [curBillingAddress, isSameAsShipping])

  /**
   *  设置Billing地址
   */
  const handleSetBillingAddress = useCallback(
    async (values: BillingAddressInput) => {
      try {
        loading.show()
        const result = await setBillingAddressesOnCartMutation({
          input: {
            cart_id: cartId,
            billing_address: values,
          },
        })
        if (result?.data?.setBillingAddressOnCart?.cart?.billing_address) {
          setCurBillingAddress(result.data.setBillingAddressOnCart.cart.billing_address)
          // 保存购物车信息
          dispatch(setBillingReady(true))
        }
        if (result?.error) {
          toast.show({
            icon: 'fail',
            content: resolveCatchMessage(result.error as TCatchMessage),
          })
        }
      } catch (e) {
        console.info(e)
      } finally {
        loading.hide()
      }
    },
    [loading, setBillingAddressesOnCartMutation, cartId, dispatch, toast],
  )

  /**
   *  设置Billing和Shipping地址一致
   */
  const handleSameAsShipping = useCallback(async () => {
    if (!isSameAsShipping) {
      setIsSameAsShipping(true)
      handleSetBillingAddress({
        same_as_shipping: true,
      })
    }
  }, [handleSetBillingAddress, isSameAsShipping])

  /**
   *  设置使用不同于Shipping的Billing地址
   */
  const handleUseDifferentBilling = useCallback(async () => {
    if (isSameAsShipping) {
      setIsSameAsShipping(false)
      dispatch(setBillingReady(false))
      setCurBillingAddress(null)
    }
  }, [dispatch, isSameAsShipping])

  /**
   *  设置Billing地址（新地址）
   */
  const onSubmit = useCallback(
    async (values: CartAddressInput) => {
      handleSetBillingAddress({
        address: values,
      })
    },
    [handleSetBillingAddress],
  )

  /**
   *  编辑Billing地址
   */
  const handleEdit = useCallback(async () => {
    setCurBillingAddress(null)
    dispatch(setBillingReady(false))
  }, [dispatch])

  /**
   *  首次加载，设置Billing和Shipping地址一致
   */
  useEffect(() => {
    handleSetBillingAddress({
      same_as_shipping: true,
    })
  }, [handleSetBillingAddress])

  return {
    isSameAsShipping,
    onSubmit,
    handleSameAsShipping,
    handleUseDifferentBilling,
    addressTitle,
    addressDescription,
    handleEdit,
    differentBillingInfoVisible,
  }
}

export default useCheckoutBilling
